import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../config/api';
import { Search, Eye, X, Database, RefreshCw, ArrowLeft, ClipboardList } from 'lucide-react';
import toast from '../utils/toast';
import SearchBar from '../components/SearchBar';
import {
  masterHeaderCard,
  masterHeaderIcon,
  masterPageShell,
  masterSearchInput,
  masterSearchWrap,
  masterTitle,
  masterSubtitle,
  masterTableData,
  masterToolbar,
  masterTableID,
  masterPanelPadding,
  masterPrimaryButton
} from '../components/masterPageStyles';

const Stock = () => {
  const [view, setView] = useState('list');
  const [stockData, setStockData] = useState([]); 
  const [medicines, setMedicines] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedMedicineBatches, setSelectedMedicineBatches] = useState([]);
  const [activeMedicineName, setActiveMedicineName] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [stockRes, medRes] = await Promise.all([
        api.get('/stock'),
        api.get('/master/medicine/all')
      ]);
      setStockData(stockRes.data || []);
      setMedicines(Array.isArray(medRes.data?.data) ? medRes.data.data : medRes.data || []);
    } catch (err) {
      toast.error("FAILED TO LOAD STOCK MASTER");
    } finally {
      setLoading(false);
    }
  };

  const medicineMap = useMemo(() => {
    const map = new Map();
    medicines.forEach((m) => {
      const id = m?.medicine_id || m?.medicine_code || m?._id;
      if (id) map.set(String(id), m.medicine_name);
    });
    return map;
  }, [medicines]);

  // Transform nested DB objects for the List View
  const processedStock = useMemo(() => {
    return stockData.map(item => ({
      ...item,
      medicine_name: medicineMap.get(item.medicine_id) || item.medicine_id,
      total_grn_received_quantity: Number((item.total_grn_received_quantity ?? item.total_stock_available) || 0),
      sale_qty: Number(item.sale_qty || 0)
    }));
  }, [stockData, medicineMap]);

  const filteredGroups = useMemo(() => {
    const sortedList = [...processedStock].sort((a, b) => {
      const idA = String(a.medicine_id || a._id || '');
      const idB = String(b.medicine_id || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((g) => {
      const medName = String(g.medicine_name || '').toLowerCase();
      const medId = String(g.medicine_id || '').toLowerCase();

      return medName.startsWith(q) || medId.startsWith(q);
    });
  }, [processedStock, searchTerm]);

  const paginatedGroups = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredGroups.slice(start, start + pageSize);
  }, [filteredGroups, currentPage, pageSize]);

  const handleOpenDetails = (group) => {
    setSelectedMedicineBatches(group.batches || []);
    setActiveMedicineName(group.medicine_name);
    setView('details');
  };

  return (
    <div className={`${masterPageShell} p-2`}>
      <header className={`${masterHeaderCard} mb-4`}>
        <div className="flex items-center gap-3">
          <div className={masterHeaderIcon}><Database size={20} /></div>
          <div>
            <h1 className={masterTitle}>Stock Inventory</h1>
            <p className={masterSubtitle}>{view === 'list' ? 'Medicine Wise Summary' : 'Batch Audit'}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {view === 'details' && (
            <button onClick={() => setView('list')} className={masterPrimaryButton}>
              <ArrowLeft size={14} /> Back to Summary
            </button>
          )}
          <button onClick={fetchData} className="p-2 hover:bg-slate-100 rounded-full">
            <RefreshCw size={18} className={loading ? 'animate-spin' : 'text-slate-500'} />
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl">
        {view === 'list' && (
          <div className="bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm">
            <SearchBar 
                className="p-2 border-b border-slate-200 flex-shrink-0 bg-white"
                value={searchTerm} 
                onChange={(val) => {
                  setSearchTerm(val);
                  setCurrentPage(1);
                }} 
                placeholder="Search alphabetically by medicine name or ID..." 
                totalItems={filteredGroups.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-300">
                    <th className={masterTableData}>SL</th>
                    <th className={masterTableData}>Medicine Name</th>
                    <th className={masterTableData}>Available</th>
                    <th className={masterTableData}>Sale Qty</th>
                    <th className={masterTableData}>Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-300 font-bold text-slate-600">
                  {paginatedGroups.map((group, index) => (
                    <tr key={group.medicine_id} className="hover:bg-emerald-50/30">
                      <td className={masterTableData}>{(currentPage - 1) * pageSize + index + 1}</td>
                      <td className={masterTableData}>
                        <div className="flex flex-col">
                          <span>{group.medicine_name}</span>
                          {/* <span className="text-[9px] text-slate-400 font-mono tracking-tighter">{group.medicine_id}</span> */}
                        </div>
                      </td>
                      <td className={masterTableData}>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-black ${group.total_grn_received_quantity > 0 ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'}`}>
                          {group.total_grn_received_quantity || 0}
                        </span>
                      </td>
                      <td className={masterTableData}>
                        <span className="text-slate-950 font-medium">{group.sale_qty}</span>
                      </td>
                     <td className={`${masterTableData} text-center`}>
                      <button 
                        onClick={() => handleOpenDetails(group)}
                        className="p-1 text-indigo-600 rounded-lg hover:bg-indigo-100 flex items-center justify-center gap-1 group mx-auto"
                      >
                        <Eye size={16} />
                        {/* <span className="text-[10px] opacity-0 group-hover:opacity-100 transition-opacity uppercase font-black">
                          Details
                        </span> */}
                      </button>
                    </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {view === 'details' && (
          <div className={`${masterPanelPadding} bg-white rounded-xl border border-slate-400 shadow-sm`}>
            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <div className="flex items-center gap-2">
                <ClipboardList size={18} className="text-indigo-600" />
                <h2 className="text-sm font-black text-slate-700 uppercase tracking-tighter">
                  Batch Breakdown: {activeMedicineName}
                </h2>
              </div>
              <button onClick={() => setView('list')} className="text-slate-400 hover:text-rose-600">
                <X size={20} />
              </button>
            </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                  <tr>
                    {/* <th className="p-3 border-r border-slate-200">Stock ID</th> */}
                    <th className="p-3 border-r border-slate-200">Batch No</th>
                    <th className="p-3 border-r border-slate-200">PO Number</th>
                    <th className="p-3 border-r border-slate-200">GRN Ref</th>
                    <th className="p-3 border-r border-slate-200">Expiry</th>
                    <th className="p-3 border-r border-slate-200 text-right">Pur. Price</th>
                    <th className="p-3 border-r border-slate-200 text-right">MRP</th>
                    <th className="p-3 border-r border-slate-200 text-right">Chalan</th>
                    <th className="p-3 text-center">Available</th>
                    <th className="p-3 text-center">Sale Qty</th>
                  </tr>
                </thead>
                <tbody className="font-bold text-slate-700 bg-white divide-y">
                  {selectedMedicineBatches.map((batch, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50">
                      {/* <td className={masterTableID}>{batch.stock_id}</td> */}
                      <td className="p-3 border-r border-slate-100 bg-amber-50/50 text-amber-700 uppercase">{batch.batch_number}</td>
                      <td className="p-3 border-r border-slate-100 text-slate-500">{batch.po_number || '-'}</td>
                      <td className="p-3 border-r border-slate-100 text-indigo-600">{batch.grn_number}</td>
                      <td className="p-3 border-r border-slate-100 text-rose-600">{batch.expiry_date}</td>
                      <td className="p-3 border-r border-slate-100 text-right">₹{batch.purchase_price}</td>
                      <td className="p-3 border-r border-slate-100 text-right text-emerald-600">₹{batch.mrp}</td>
                      <td className="p-3 border-r border-slate-100 text-right">{batch.total_received}</td>

                      <td className="p-3 text-center">
                         <span className="px-3 py-1 bg-slate-800 text-white rounded font-black text-[10px]">
                          {Number((batch.total_grn_received_quantity ?? batch.quantity_available) || 0)}
                         </span>
                      </td>
                      <td className="p-3 text-center text-slate-900">{Number(batch.sale_qty || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Stock;
